import _ from 'lodash';
import {BigNumber} from 'bignumber.js';

import purchaseOrderEnums from 'goodeggs-purchase-orders-sdk/purchase_order/enums';
import catalogHelpers from 'goodeggs-purchase-orders-sdk/catalog/helpers';

const formValueConfig = {
  number: {
    get() {
      return this.lineItem.number;
    },
    set(value) {
      return {number: value};
    },
  },
  status: {
    get() {
      return this.lineItem.status;
    },
    set(value) {
      return {status: value};
    },
  },
  updatedAt: {
    get() {
      return this.lineItem.updatedAt;
    },
  },
  catalogProduct: {
    get() {
      return this.getCatalogProduct(this.lineItem.catalogId);
    },
    set(catalogProduct) {
      return {
        catalogId: catalogProduct._id,
        type: catalogProduct.type != null ? catalogProduct.type : 'product',
        unitLabel: catalogProduct.unitLabel,
        unitQuantity: catalogProduct.unitQuantity,
        unitQuantityLabel: catalogProduct.unitQuantityLabel,
      };
    },
    validate(value) {
      if (value == null) {
        return 'must choose a catalog product';
      }
      // Validation that Purchasing UOMs are cached, as the catalog product may have a purchasingOptionId
      // that has been assigned and integrated with riversand, but is not yet valid and/or cached in mongo.
      // We can assume that if the Purchasing UOM is cached the related sku is also cached.
      // Prevents downstream errors when the purchase order is integrated with WMS for skus that don't yet exist.
      // Note we are not using WMS for `sfbay` foodhub so we don't need to validate there
      if (
        this.purchaseOrder.foodhubSlug !== 'sfbay' &&
        (value.purchasingOptionId == null ||
          this.getPurchasingUom(value.purchasingOptionId) == null)
      ) {
        return 'Selected catalog product does not have a valid Purchasing UOM';
      }
    },
    isEditable() {
      return this.purchaseOrder.status === 'new' || this.status === 'new';
    },
  },
  startSellingWhen: {
    get() {
      return this.lineItem.startSellingWhen;
    },
    set(value) {
      return {startSellingWhen: value};
    },
    validate(value) {
      if (value == null) {
        return 'field required';
      }
      const daysBeforeItemCanBeSold = this.getStartSellingWhenInDays(value);
      if (
        daysBeforeItemCanBeSold >
        this.catalogProduct.shelfLife - this.catalogProduct.customerRequiredLeadTime
      ) {
        return 'projected purge day to occur before product can be sold on the marketplace';
      }
    },
    isEditable() {
      return !purchaseOrderEnums.terminalStatuses.includes(this.status) && !this.hasReceipts();
    },
  },
  unitPrice: {
    get() {
      return this.getUnitPrice();
    },
    set(value) {
      return {unitPrice: value};
    },
    validate(value) {
      if (this.purchaseOrder.orderType === 'consignment') {
        if (value == null) {
          return 'field required';
        }
      } else {
        if (!(value > 0)) {
          return 'must be greater than 0';
        }
      }
    },
    isEditable() {
      return !purchaseOrderEnums.terminalStatuses.includes(this.status) && !this.hasReceipts();
    },
  },
  orderQuantity: {
    get() {
      return this.lineItem[this.getCurrentQuantityField()];
    },
    set(value) {
      return {
        orderQuantity: value,
        // if lineItem is confirmed, set confirmedOrder to updated orderQuantity
        // to avoid temporarily flipping confirmedQuantity from prevConfirmedQuantity -> unknown -> newConfirmedQuantity
        confirmedQuantity: this.lineItem.status === 'confirmed' ? value : undefined,
      };
    },
    validate(value) {
      if (!(value >= 1)) {
        return 'must be at least 1';
      }
      const valueInCatalogUnits = this.convertQuantityToCatalogUnits(value);
      if (!(valueInCatalogUnits % 1 === 0)) {
        return 'must not be fractional';
      }
    },
    isEditable() {
      return !purchaseOrderEnums.terminalStatuses.includes(this.status) && !this.hasReceipts();
    },
  },
  fromDay: {
    get() {
      return null;
    },
  },
  toDay: {
    get() {
      return null;
    },
  },
  processing: {
    get() {
      return false;
    },
  },
};

/**
 * Form that manages inputs for an individual lineItem
 * - converts lineItem to form data
 * - answers questions about form data (but does not tell you how to display form data)
 * - assigns form data to lineItem
 */
export default class LineItemForm {
  constructor(lineItem, purchaseOrder, purchaseOrderMetaData) {
    let $injector;
    this._stopListeningForNewItemSave = this._stopListeningForNewItemSave.bind(this);
    this.catalogProductSaved = this.catalogProductSaved.bind(this);
    this.lineItem = lineItem;
    this.purchaseOrder = purchaseOrder;
    this.purchaseOrderMetaData = purchaseOrderMetaData;
    ({
      getPurchasingUom: this.getPurchasingUom,
      supplier: this.supplier,
      getCatalogProduct: this.getCatalogProduct,
      loadCatalogProducts: this.loadCatalogProducts,
      getPurchaseOrderProductByLegacyId: this.getPurchaseOrderProductByLegacyId,
      $injector,
    } = this.purchaseOrderMetaData);
    this.Promise = $injector.get('Promise');
    this.$window = $injector.get('$window');
    this.$state = $injector.get('$state');
    this._mapLineItemDataToForm();
  }

  _mapLineItemDataToForm() {
    const form = {};
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      form[formValue] = config.get.call(this);
    }
    this._originalFormData = form;
    return _.assign(this, form);
  }

  _mapFormToLineItem() {
    const result = {};
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      _.assign(result, config.set != null ? config.set.call(this, this[formValue]) : undefined);
    }
    return result;
  }

  isEdited() {
    const formFields = Object.keys(this._originalFormData);
    return _.some(formFields, (field) => this.isFieldEdited(field));
  }

  isValid() {
    // eslint-disable-next-line guard-for-in
    for (const formValue in formValueConfig) {
      const config = formValueConfig[formValue];
      const errorMessage =
        config.validate != null ? config.validate.call(this, this[formValue]) : undefined;
      if (errorMessage != null) {
        return false;
      }
    }
    return true;
  }

  isFieldEditable(field) {
    const fieldConfig = formValueConfig[field];
    if (typeof fieldConfig.isEditable === 'function') {
      return fieldConfig.isEditable.call(this);
    }
    return true;
  }

  validateField(field) {
    const fieldConfig = formValueConfig[field];
    if (typeof fieldConfig.validate === 'function') {
      return fieldConfig.validate.call(this, this[field]);
    }
    return undefined;
  }

  isFieldEdited(field) {
    return this._originalFormData[field] !== this[field];
  }

  canRemove() {
    return !purchaseOrderEnums.terminalStatuses.includes(this.status) && !this.hasReceipts();
  }

  _stopListeningForNewItemSave() {
    if (this.newItemWindow != null) {
      this.newItemWindow.removeEventListener('catalogProductSave', this.catalogProductSaved);
    }
    this.newItemWindow = null;
  }

  catalogProductSaved(e) {
    const {catalogItemId} = e.detail;
    this.newItemWindow.close();
    this._stopListeningForNewItemSave();
    this.$window.focus();

    return this.loadCatalogProducts().then(
      () =>
        // after updating the catalog products, select the new one
        (this.catalogProduct = this.getCatalogProduct(catalogItemId)),
    );
  }

  catalogProductSelected($item) {
    this.updateUnitPrice();

    this._stopListeningForNewItemSave(); // stop paying attention to a previous new item window

    if ($item._id === 'newItem') {
      this.catalogProduct = null; // clear out the newItem selection from the typeahead

      const newItemUrl = this.$state.href('catalog.supplier.item.new', {
        supplierSlug: this.supplier.slug,
        notifyOnSave: true,
      });
      this.newItemWindow = this.$window.open(newItemUrl);
      this.newItemWindow.addEventListener('catalogProductSave', this.catalogProductSaved);
    }

    return true;
  }

  /**
   * @return {Number} line item total price in cents
   */
  getTotalPrice() {
    if (!this.isValid()) {
      return 0;
    }
    // use the latest quantity in the line item form if we might be editing order quantity,
    // otherwise use the current (immutable) quantity field
    const quantityField = this.getCurrentQuantityField();
    let quantity =
      quantityField === 'orderQuantity' ? this[quantityField] : this.lineItem[quantityField];
    quantity = this.convertQuantityToCatalogUnits(quantity);
    return new BigNumber(this.unitPrice).times(quantity).toNumber();
  }

  getCurrentQuantityField() {
    switch (this.status) {
      case 'closed':
        return 'receivedQuantity';
      case 'confirmed':
        return 'confirmedQuantity';
      default:
        return 'orderQuantity';
    }
  }

  getStartSellingWhenInDays(startSellingWhen) {
    switch (startSellingWhen) {
      case 'confirmed (day after delivery day)':
        return 1;
      case 'confirmed (2 days after delivery day)':
        return 2;
      case 'confirmed (3 days after delivery day)':
        return 3;
      default:
        // sell when received and confirmed (delivery day)
        return 0;
    }
  }

  getOriginalFormValue(field) {
    return this._originalFormData[field];
  }

  getUnitPrice() {
    if (this.lineItem.unitPrice != null) {
      return this.lineItem.unitPrice;
    }
    if (this.catalogProduct != null && this.catalogProduct.orderUnitPrice != null) {
      return this.catalogProduct.orderUnitPrice;
    }
    return 0;
  }

  updateUnitPrice() {
    if (this.catalogProduct != null && this.catalogProduct.orderUnitPrice != null) {
      this.unitPrice = this.catalogProduct.orderUnitPrice;
    } else {
      this.unitPrice = 0;
    }
  }

  convertQuantityToCatalogUnits(quantity) {
    if (quantity == null) {
      quantity = 0;
    }
    if (!_.isNumber(quantity)) {
      throw new Error('quantity value must be a number');
    }
    if (this.catalogProduct == null) {
      return 0;
    }
    return catalogHelpers.unitConverter(this.catalogProduct).convertTo(quantity, 'orderQuantity');
  }

  /**
   * Assign form data to lineItem and save.
   * - if purchase order isnt new, create a new lineItem and mark the original as "edited"
   * @resolve {Array} updatedLineItems
   */
  save({dropoffDay, dropoffWindow} = {}) {
    let newLineItemValues;

    return (
      this.Promise.try(() => {
        newLineItemValues = this._mapFormToLineItem();
        newLineItemValues.dropoffDay = dropoffDay;
        newLineItemValues.dropoffWindow = dropoffWindow;
        if (!this.isValid()) {
          throw new Error('line item invalid');
        }
        if (!this.isEdited()) {
          return this.lineItem;
        }
      })
        // Temporary shim to augment Line Item with some information from `PurchaseOrderProduct`
        // (future canonical source of truth) while the legacy `CatalogProduct` is still the primary
        // source of truth.
        .tap((lineItem) => {
          // TODO(serhalp) figure out how to get async/await to work with angular digest/$timeout/etc.
          // Conditionals in .then chains are difficult and error prone.
          if (lineItem != null) return;
          return this.getPurchaseOrderProductByLegacyId(newLineItemValues.catalogId).then(
            (purchaseOrderProduct) => {
              newLineItemValues.purchaseOrderProduct = purchaseOrderProduct;
            },
          );
        })
        .then((lineItem) => {
          // TODO(serhalp) figure out how to get async/await to work with angular digest/$timeout/etc.
          // Conditionals in .then chains are difficult and error prone.
          if (lineItem != null) return lineItem;

          if (
            this.purchaseOrder.status === 'new' ||
            this.lineItem.status === 'new' ||
            newLineItemValues.status === 'cancelled'
          ) {
            lineItem = _.assign(this.lineItem, newLineItemValues);
            if (this.lineItem._id) {
              return lineItem.$update();
            }
            return lineItem.$save();
          }
          // lineItem was edited, create a new one
          // TODO this should be a server side method
          const newLineItem = (() => {
            let result = angular.copy(this.lineItem);
            delete result._id;
            result = _.assign(result, newLineItemValues);
            // editing confirmed lineItems shouldn't revert it's status to `new`
            // to avoid temporarily flipping status from `confirmed` -> `new` -> `confirmed`
            if (this.lineItem.status !== 'confirmed') {
              result.status = 'new';
              delete result.confirmedQuantity;
            }
            result.parentId = this.lineItem._id;
            result.editSequenceNumber = this.purchaseOrder.editSequenceNumber;
            return result;
          })();
          this.lineItem.status = 'edited';

          // Note: updating lineItem's status before creating a new lineItem to avoid race condition
          // where a new lineItem could be created before the current one is update.
          // One of the issues that could pose is `duplicateKeyError`, as two lineItems could have
          // status:"submitted".
          return this.lineItem
            .$update()
            .then(() => newLineItem.$save().then(() => [this.lineItem, newLineItem]));
        })
        .then(function(lineItems) {
          if (!_.isArray(lineItems)) {
            return [lineItems];
          }
          return lineItems;
        })
    );
  }

  destroy() {
    if (this.lineItem._id) {
      return this.lineItem.$destroy();
    }
  }

  cancel() {
    this.editSequenceNumber = this.purchaseOrder.editSequenceNumber;
    this.status = 'cancelled';
  }

  hasReceipts() {
    return this.lineItem.receivedQuantity != null;
  }
}
